import React from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { TrendingUp, Calendar, CalendarDays, Sun } from "lucide-react";

const fmtDay = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const Mini = ({ icon: Icon, value, label, testid }) => (
  <div data-testid={testid} className="bg-[#1A1A1A] border border-[#374151] p-6">
    <Icon className="h-5 w-5 text-[#D86C1E]" />
    <div className="font-heading text-4xl font-black text-white mt-4">{value}</div>
    <div className="text-xs uppercase tracking-wider text-white/50 mt-1">{label}</div>
  </div>
);

export default function AnalyticsTab({ data }) {
  if (!data) {
    return <div className="text-white/40 py-16 text-center animate-pulse">Loading analytics…</div>;
  }
  const barData = data.leads_by_service.slice(0, 8);

  return (
    <div data-testid="analytics-tab" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Mini icon={Sun} value={data.today} label="Today" testid="an-today" />
        <Mini icon={Calendar} value={data.this_week} label="This Week" testid="an-week" />
        <Mini icon={CalendarDays} value={data.this_month} label="This Month" testid="an-month" />
        <Mini icon={TrendingUp} value={data.total_leads} label="All Time" testid="an-total" />
      </div>

      <div className="bg-[#1A1A1A] border border-[#374151] p-6">
        <h3 className="font-heading font-bold text-white mb-1">Leads — Last 30 Days</h3>
        <p className="text-xs text-white/40 mb-6">Every lead is attributed to agency-driven traffic.</p>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data.leads_by_day} margin={{ left: -20, right: 8 }}>
            <defs>
              <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D86C1E" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#D86C1E" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#666" fontSize={11} interval={4} />
            <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "#0A0A0A", border: "1px solid #374151", color: "#fff" }}
              labelFormatter={fmtDay}
            />
            <Area type="monotone" dataKey="count" stroke="#D86C1E" strokeWidth={2} fill="url(#g)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-[#1A1A1A] border border-[#374151] p-6">
        <h3 className="font-heading font-bold text-white mb-6">Most Requested Services</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={barData} layout="vertical" margin={{ left: 60, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" horizontal={false} />
            <XAxis type="number" stroke="#666" fontSize={11} allowDecimals={false} />
            <YAxis type="category" dataKey="service" stroke="#999" fontSize={11} width={120} />
            <Tooltip
              cursor={{ fill: "rgba(216,108,30,0.08)" }}
              contentStyle={{ background: "#0A0A0A", border: "1px solid #374151", color: "#fff" }}
            />
            <Bar dataKey="count" radius={[0, 2, 2, 0]}>
              {barData.map((_, i) => (
                <Cell key={i} fill="#D86C1E" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
